import { API } from '../../../../utility/API'

//************************************//
// Get available staking options
export const _getPublicStakingProducts = (
  { page = 1, limit = 10, searchQuery = null, matchAsset = null, duration = null, productName = null, currencyId = null, tag = null },
  callback,
  callbackErr
) => {
  const params = {
    page,
    limit
  }

  // Add search parameters if provided
  if (searchQuery) {
    params['search[currency_id]'] = searchQuery
    params['search[currency_name]'] = searchQuery
  }

  // Add tag filter if provided
  if (tag && tag !== 'all') {
    params['tag'] = tag
  }

  // Add optional filters
  if (matchAsset !== null) params.match_asset = matchAsset
  if (duration !== null) params.duration = duration
  if (productName) params.product_name = productName
  if (currencyId) params.currency_id = currencyId

  API.get(`/trading/earn/staking_periods`, { params })
    .then(function ({ data, headers }) {
      callback({ data, page: headers['page'], total: headers['total'] })
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}

//************************************//
// Create staking subscription
export const _createStakingSubscription = (
  { stakingPeriodId, amount, stakingProviderId = 1 },
  callback,
  callbackErr
) => {
  API.post(`/trading/account/staking/new`, {
    staking_period_id: stakingPeriodId,
    amount,
    staking_provider_id: stakingProviderId
  })
    .then(function (response) {
      callback(response.data)
    })
    .catch(function (error) {
      console.error('Error creating staking subscription:', error)

      // Enhanced error handling
      let errorMessage = 'Failed to create staking subscription'
      let errorData = null

      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response
        errorData = data

        switch (status) {
          case 400:
            errorMessage = data?.message || data?.error || 'Invalid request parameters'
            break
          case 401:
            errorMessage = 'Authentication required. Please log in again.'
            break
          case 403:
            errorMessage = 'Access denied. You don\'t have permission to perform this action.'
            break
          case 404:
            errorMessage = 'Staking product not found or no longer available'
            break
          case 409:
            errorMessage = data?.message || 'Conflict: You may already have an active subscription for this product'
            break
          case 422:
            errorMessage = data?.message || 'Validation failed. Please check your input.'
            break
          case 429:
            errorMessage = 'Too many requests. Please try again later.'
            break
          case 500:
            errorMessage = 'Server error. Please try again later.'
            break
          case 503:
            errorMessage = 'Service temporarily unavailable. Please try again later.'
            break
          default:
            errorMessage = data?.message || data?.error || `Server error (${status})`
        }

        // Handle specific error formats
        if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          errorMessage = data.errors[0]
        } else if (data?.validation_errors) {
          const validationErrors = Object.values(data.validation_errors).flat()
          errorMessage = validationErrors[0] || errorMessage
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.'
      } else {
        // Other error
        errorMessage = error.message || 'An unexpected error occurred'
      }

      callbackErr({
        ...error.response,
        message: errorMessage,
        originalError: error,
        errorData
      })
    })
}

//************************************//
// Estimate staking reward
export const _estimateStakingReward = (
  { amount, stakingPeriodId },
  callback,
  callbackErr
) => {
  API.post(`/trading/account/staking/estimate_reward`, {
    amount,
    staking_period_id: stakingPeriodId
  })
    .then(function (response) {
      callback(response.data)
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}

//************************************//
// Unstake flexible subscription
export const _unstakeSubscription = (
  { id, amount, fullAmount = false },
  callback,
  callbackErr
) => {
  const params = { id }

  if (fullAmount) {
    params.full_amount = true
  } else {
    params.amount = amount
  }

  API.post(`/trading/account/unstake`, params)
    .then(function (response) {
      callback(response.data)
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}

//************************************//
// Early redeem for fixed subscriptions
export const _redeemEarlySubscription = (
  { id },
  callback,
  callbackErr
) => {
  API.post(`/trading/account/staking/redeem_early`, { id })
    .then(function (response) {
      callback(response.data)
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}

//************************************//
// Get APY for calculator
export const _getStakingAPY = (
  { currencyId, duration },
  callback,
  callbackErr
) => {
  API.get(`/trading/account/staking/apr`, {
    params: {
      currency_id: currencyId,
      duration
    }
  })
    .then(function (response) {
      callback(response.data)
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}

//************************************//
// Get user staking portfolio
export const _getUserStakingPortfolio = (
  callback,
  callbackErr
) => {
  API.get(`/trading/account/staking/user_portfolio`)
    .then(function (response) {
      callback(response.data)
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}

//************************************//
// Legacy function - keeping for compatibility
export const _getStakingDurations = (callback, callbackErr) => {
  API.get(`/trading/earn/durations`)
    .then(function (response) {
      callback(response)
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}

//************************************//
// Get user account balances
export const _getUserBalances = (
  callback,
  callbackErr
) => {
  API.get(`/trading/account/balances`)
    .then(function (response) {
      callback(response.data)
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}

//************************************//
// Get staking coins
export const _getStakingCoins = (params = {}, callback, callbackErr) => {
  const defaultParams = {
    // 'search[status]': 'enabled' // Only show enabled coins by default
  }

  const finalParams = { ...defaultParams, ...params }

  API.get('/trading/earn/staking_coin', { params: finalParams })
    .then(response => {
      if (callback) callback(response.data)
    })
    .catch(error => {
      console.error('Error fetching staking coins:', error)
      if (callbackErr) callbackErr(error)
    })
}

//************************************//
// Get estimated staking reward
export const _getStakingEstimatedReward = (params, callback, callbackErr) => {
  API.post('/trading/account/staking/estimate_reward', params)
    .then(response => {
      if (callback) callback(response.data)
    })
    .catch(error => {
      console.error('Error fetching estimated reward:', error)
      if (callbackErr) callbackErr(error)
    })
}

//************************************//
// Get staking tags
export const _getStakingTags = (
  { tag = null } = {},
  callback,
  callbackErr
) => {
  const params = {}

  // Add tag filter if provided
  if (tag) {
    params.tag = tag
  }

  API.get(`/trading/earn/staking_tag`, { params })
    .then(function (response) {
      callback(response.data)
    })
    .catch(function (error) {
      callbackErr(error.response)
    })
}
